"use client";

import { Trash2Icon } from "lucide-react";
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type Ref,
} from "react";

import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";

type KanbanColumnStyle = CSSProperties & {
  "--kanban-column-accent": string;
};

/**
 * Presentational kanban shells. RecordBoardLens and BoardColumn keep the live
 * machinery — grouping, virtualization, drag-and-drop, mutations, load-more —
 * and compose these; surfaces without live data (print, export, the marketing
 * product bridge) compose them from fixtures, and their module graph never
 * reaches the record-field renderers or the workspace client.
 */

/** The board: on a phone, a column picker over one column; from sm up, the columns side by side, scrolling sideways. */
export function RecordBoardLensView({
  picker,
  mobileColumn,
  children,
}: {
  /** The phone's column picker. */
  picker?: ReactNode;
  /** The column the phone shows. */
  mobileColumn?: ReactNode;
  /** The columns from sm up. */
  children?: ReactNode;
}) {
  return (
    // Follow the owning channel surface: standalone channels use --background,
    // while embedded session artifacts use the distinct --surface canvas.
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--channel-canvas)]">
      <div className="flex min-h-0 flex-1 flex-col sm:hidden">
        <div className="border-b border-border/60 bg-[var(--channel-canvas)] px-3 py-2">
          {picker}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden p-3">{mobileColumn}</div>
      </div>
      <div className="hidden min-h-0 flex-1 overflow-x-auto overflow-y-hidden scrollbar-thin sm:block">
        <div className="flex h-full gap-3 p-3">{children}</div>
      </div>
    </section>
  );
}

/** The phone's column picker: each column with its count. */
export function BoardColumnPickerView({
  columns,
  selected,
  onSelect,
}: {
  columns: readonly { key: string; title: string; count: number }[];
  /** The shown column's key. */
  selected: string;
  onSelect?: (key: string) => void;
}) {
  return (
    <>
      <label className="sr-only" htmlFor="mobile-board-column">
        Board column
      </label>
      <NativeSelect
        id="mobile-board-column"
        className="rounded-md px-2 text-sm font-medium"
        {...(onSelect
          ? {
              value: selected,
              onChange: (event) => onSelect(event.target.value),
            }
          : { defaultValue: selected })}
      >
        {columns.map((column) => (
          <option key={column.key} value={column.key}>
            {column.title} ({column.count})
          </option>
        ))}
      </NativeSelect>
    </>
  );
}
export const BoardColumnView = forwardRef<
  HTMLElement,
  {
    title: string;
    /** The column accent, written as `--kanban-column-accent`. */
    color: string;
    count: number;
    /** Preformatted column subtotal; omitted when there is nothing to sum. */
    subtotal?: string | null;
    mobile?: boolean;
    children?: ReactNode;
  } & Omit<ComponentPropsWithoutRef<"section">, "color" | "title">
>(function BoardColumnView(
  {
    title,
    color,
    count,
    subtotal,
    mobile = false,
    className,
    style,
    children,
    ...sectionProps
  },
  ref,
) {
  const accentStyle: KanbanColumnStyle = {
    "--kanban-column-accent": color,
    ...style,
  };
  return (
    <section
      ref={ref}
      className={cn(
        "mlx-kanban-column",
        mobile ? "mlx-kanban-column-mobile" : "",
        className,
      )}
      style={accentStyle}
      {...sectionProps}
    >
      <div className="mlx-kanban-header">
        <div className="mlx-kanban-header-title">
          <span className="mlx-kanban-header-dot" />
          <h2 className="mlx-kanban-header-label">{title}</h2>
          <span className="text-xs text-muted-foreground">({count})</span>
        </div>
        {subtotal ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {subtotal}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
});

/**
 * A column's cards. The application places each at the height the virtualizer
 * measured; without a height and starts they stack in order, which is where
 * the measured cards land.
 */
export function BoardCardListView({
  height,
  children,
}: {
  /** The virtualizer's total height. */
  height?: number;
  children?: ReactNode;
}) {
  return (
    <ul
      className="relative shrink-0"
      style={height === undefined ? undefined : { height }}
    >
      {children}
    </ul>
  );
}

/** A card's place in its column: at `start` when placed, else next in order. */
export function BoardCardItemView({
  index,
  start,
  ref,
  children,
}: {
  index: number;
  /** The card's top in the column, in pixels. */
  start?: number;
  ref?: Ref<HTMLLIElement>;
  children?: ReactNode;
}) {
  return (
    <li
      data-index={index}
      ref={ref}
      className={start === undefined ? "pb-2" : "absolute inset-x-0 pb-2"}
      style={
        start === undefined
          ? undefined
          : { transform: `translateY(${start}px)` }
      }
    >
      {children}
    </li>
  );
}

/** A column with no cards. */
export function BoardColumnEmptyView({ children }: { children?: ReactNode }) {
  return (
    <p className="px-3 py-5 text-center text-xs text-muted-foreground">
      {children}
    </p>
  );
}

export function BoardCardView({
  title,
  sumText,
  chips,
  footer,
  actions,
  openable = false,
  onOpen,
  className,
  ...divProps
}: {
  title: string;
  /** Preformatted sum-field line under the title. */
  sumText?: string | null;
  /** The chip row's content; the row renders only when provided. */
  chips?: ReactNode;
  /** Below the body — the mobile move control in the application. */
  footer?: ReactNode;
  /** Absolutely-positioned controls — the delete button in the application. */
  actions?: ReactNode;
  /** The card body is the button that opens the record, as it is wherever `onOpen` is set; a static board sets this instead. */
  openable?: boolean;
  /** When set, the card body is a button opening the record. */
  onOpen?: () => void;
} & Omit<ComponentPropsWithoutRef<"div">, "title">) {
  const body = (
    <>
      <div className="truncate text-sm font-semibold leading-tight">
        {title}
      </div>
      {sumText ? (
        <div className="mt-1.5 text-sm tabular-nums text-muted-foreground">
          {sumText}
        </div>
      ) : null}
      {chips ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/40 pt-2 text-xs text-muted-foreground">
          {chips}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={cn("mlx-kanban-card group/card", className)} {...divProps}>
      {onOpen || openable ? (
        <button
          type="button"
          className={cn("block w-full text-left", actions ? "pr-8" : "")}
          onClick={onOpen}
        >
          {body}
        </button>
      ) : (
        <div className={cn("block w-full text-left", actions ? "pr-8" : "")}>
          {body}
        </div>
      )}
      {footer}
      {actions}
    </div>
  );
}

/** A card's "Move to..." control: the other columns, shown on hover from sm up and always on a phone. */
export function BoardCardMoveView({
  targets,
  mobile = false,
  disabled = false,
  onMove,
}: {
  targets: readonly { key: string; title: string }[];
  mobile?: boolean;
  /** The card is moving. */
  disabled?: boolean;
  onMove?: (key: string) => void;
}) {
  return (
    <label
      className={cn(
        "mt-3 block text-xs font-medium text-muted-foreground transition-opacity",
        !mobile &&
          "opacity-0 group-hover/card:opacity-100 focus-within:opacity-100",
      )}
    >
      <span className="sr-only">Move record</span>
      <NativeSelect
        className="rounded-md px-2 text-sm font-medium"
        defaultValue=""
        onChange={(event) => {
          const target = event.currentTarget.value;
          event.currentTarget.value = "";
          if (target) onMove?.(target);
        }}
        disabled={disabled}
      >
        <option value="" disabled>
          Move to...
        </option>
        {targets.map((target) => (
          <option key={target.key} value={target.key}>
            {target.title}
          </option>
        ))}
      </NativeSelect>
    </label>
  );
}

/** A card's delete button, top right: shown on hover from sm up and always on a phone. */
export function BoardCardDeleteButton({
  mobile = false,
  disabled = false,
  onDelete,
}: {
  mobile?: boolean;
  /** The record is being deleted. */
  disabled?: boolean;
  onDelete?: () => void;
}) {
  return (
    <Button
      size="icon-xs"
      variant="ghost"
      aria-label="Delete record"
      title="Delete record"
      disabled={disabled}
      className={cn(
        "absolute right-1.5 top-1.5 text-muted-foreground transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover/card:opacity-100",
        mobile ? "opacity-100" : "opacity-0",
      )}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onDelete?.();
      }}
    >
      <Trash2Icon />
    </Button>
  );
}
